import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import {
  ApiClientError,
  deleteTeacherCohort,
  getTeacherCourses,
  getTeacherCohorts,
  patchTeacherCohort,
  postTeacherCohort,
  type TeacherCohortRecord,
  type TeacherCourseRecord,
} from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";
import { CohortCourseThumb } from "./CohortCourseThumb";
import { CohortLessonProgressBar } from "./CohortLessonProgressBar";
import { buildDeleteConcludedCohortConfirmMessage } from "./cohortDeleteMessages";
import { teacherCohortProgressUrl } from "./submissions/cohortProgressUrl";

function CohortRows({
  cohorts,
  coursesById,
  concludingId,
  deletingId,
  onConclude,
  onDelete,
  showProgress,
}: {
  readonly cohorts: TeacherCohortRecord[];
  readonly coursesById: Map<string, TeacherCourseRecord>;
  readonly concludingId: string | null;
  readonly deletingId: string | null;
  readonly onConclude?: (cohort: TeacherCohortRecord) => void;
  readonly onDelete?: (cohort: TeacherCohortRecord) => void;
  readonly showProgress: boolean;
}) {
  if (cohorts.length === 0) return null;

  return (
    <>
      {cohorts.map((c) => {
        const course = coursesById.get(c.courseId);
        const heroImageUrl = c.courseHeroImageUrl ?? course?.heroImageUrl ?? null;
        const accentColor = c.courseAccentColor ?? course?.accentColor ?? null;

        return (
        <tbody key={c.id} className="cohort-table-group">
          <tr className="cohort-table-main-row">
            <td className="cohort-table-course-cell">
              <CohortCourseThumb
                title={c.courseTitle}
                heroImageUrl={heroImageUrl}
                accentColor={accentColor}
              />
            </td>
            <td>
              <strong>{c.label}</strong>
            </td>
            <td className="cohort-table-students">{c.studentCount ?? 0}</td>
            <td className="muted small">
              {c.status === "archived" && c.archivedAt
                ? `Concluded ${new Date(c.archivedAt).toLocaleDateString()}`
                : new Date(c.updatedAt).toLocaleString()}
            </td>
            <td className="teacher-table-actions cohort-table-actions">
              {c.status === "active" ? (
                <>
                  <Link
                    to={teacherCohortProgressUrl(c.id)}
                    className="btn secondary tight"
                    style={{ textDecoration: "none" }}
                  >
                    Progress
                  </Link>
                  <Link
                    to={`/teacher/cohorts/${c.id}`}
                    className="btn primary tight"
                    style={{ textDecoration: "none" }}
                  >
                    Manage
                  </Link>
                  {onConclude ? (
                    <button
                      type="button"
                      className="btn ghost tight"
                      disabled={concludingId !== null}
                      onClick={() => onConclude(c)}
                    >
                      {concludingId === c.id ? "Concluding…" : "Conclude"}
                    </button>
                  ) : null}
                </>
              ) : (
                <>
                  <Link
                    to={`/teacher/cohorts/${c.id}`}
                    className="btn secondary tight"
                    style={{ textDecoration: "none" }}
                  >
                    View
                  </Link>
                  {onDelete ? (
                    <button
                      type="button"
                      className="btn secondary danger tight"
                      disabled={deletingId !== null}
                      onClick={() => onDelete(c)}
                    >
                      {deletingId === c.id ? "Deleting…" : "Delete"}
                    </button>
                  ) : null}
                </>
              )}
            </td>
          </tr>
          {showProgress ? (
            <tr className="cohort-table-progress-row">
              <td colSpan={5}>
                <CohortLessonProgressBar
                  progress={c.lessonProgress ?? { planned: 0, assigned: 0, completed: 0 }}
                />
              </td>
            </tr>
          ) : null}
        </tbody>
        );
      })}
    </>
  );
}

export default function TeacherCohortsListPage() {
  const { getBearerToken } = useAuth();
  const bearer = getBearerToken();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefillTemplateId = searchParams.get("courseId") ?? "";
  const showCreate = searchParams.get("create") === "1" || Boolean(prefillTemplateId);

  const [cohorts, setCohorts] = useState<TeacherCohortRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [courses, setCourses] = useState<TeacherCourseRecord[]>([]);
  const [allCourses, setAllCourses] = useState<TeacherCourseRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [concludingId, setConcludingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const [pickTemplateId, setPickTemplateId] = useState(prefillTemplateId);
  const [newLabel, setNewLabel] = useState("");
  const [seedLessonPlans, setSeedLessonPlans] = useState(true);
  const [creating, setCreating] = useState(false);
  const [upgradeCta, setUpgradeCta] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(showCreate);

  const refresh = useCallback(async () => {
    if (!bearer) return;
    setError(null);
    setLoading(true);
    try {
      const [cohortData, templateData] = await Promise.all([
        getTeacherCohorts(bearer, { limit: 200, offset: 0 }),
        getTeacherCourses(bearer, { limit: 200, offset: 0 }),
      ]);
      setCohorts(cohortData.cohorts);
      setTotal(cohortData.page.total);
      setAllCourses(templateData.courses);
      const activeTemplates = templateData.courses.filter((t) => t.status === "active");
      setCourses(activeTemplates);
      if (!pickTemplateId && activeTemplates[0]) {
        setPickTemplateId(activeTemplates[0].id);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load cohorts");
      setCohorts([]);
    } finally {
      setLoading(false);
    }
  }, [bearer]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (prefillTemplateId) setPickTemplateId(prefillTemplateId);
  }, [prefillTemplateId]);

  useEffect(() => {
    if (showCreate) setCreateOpen(true);
  }, [showCreate]);

  const activeCohorts = useMemo(() => cohorts.filter((c) => c.status === "active"), [cohorts]);
  const previousCohorts = useMemo(() => cohorts.filter((c) => c.status === "archived"), [cohorts]);
  const coursesById = useMemo(() => new Map(allCourses.map((c) => [c.id, c])), [allCourses]);

  function openCreateModal() {
    setCreateOpen(true);
  }

  function closeCreateModal() {
    if (creating) return;
    setCreateOpen(false);
    if (showCreate) {
      navigate("/teacher/cohorts", { replace: true });
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!bearer || !pickTemplateId || !newLabel.trim()) return;
    setCreating(true);
    setError(null);
    setUpgradeCta(null);
    try {
      const { cohort } = await postTeacherCohort(bearer, {
        courseId: pickTemplateId,
        label: newLabel.trim(),
        seedLessonPlans,
      });
      setNewLabel("");
      setCreateOpen(false);
      navigate(`/teacher/cohorts/${cohort.id}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Create failed";
      setError(msg);
      if (err instanceof ApiClientError && (err.code === "BILLING_REQUIRED" || err.code === "COHORT_LIMIT_REACHED")) {
        setUpgradeCta("Upgrade your plan in Billing to create more cohorts.");
      }
    } finally {
      setCreating(false);
    }
  }

  async function handleConclude(cohort: TeacherCohortRecord) {
    if (!bearer || concludingId) return;
    const ok = window.confirm(
      `Conclude "${cohort.label}"? Students can no longer join. The cohort moves to Previous cohorts for your records.`
    );
    if (!ok) return;
    setConcludingId(cohort.id);
    setError(null);
    try {
      await patchTeacherCohort(bearer, cohort.id, { archived: true });
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not conclude cohort");
    } finally {
      setConcludingId(null);
    }
  }

  async function handleDelete(cohort: TeacherCohortRecord) {
    if (!bearer || deletingId) return;
    if (!window.confirm(buildDeleteConcludedCohortConfirmMessage(cohort.label))) return;
    setDeletingId(cohort.id);
    setError(null);
    try {
      await deleteTeacherCohort(bearer, cohort.id);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not delete cohort");
    } finally {
      setDeletingId(null);
    }
  }

  const tableHead = (
    <thead>
      <tr>
        <th>Course</th>
        <th>Cohort</th>
        <th>Students</th>
        <th>Updated</th>
        <th />
      </tr>
    </thead>
  );

  return (
    <div className="teacher-page submissions-dashboard">
      <header className="submissions-hero overview-hero">
        <div className="submissions-hero-text">
          <p className="eyebrow">Teacher · Cohorts</p>
          <h1 className="teacher-page-title">Cohorts</h1>
          <p className="teacher-page-lede">
            Live teaching groups tied to a course: rosters, lesson plans, and assigned lessons.{" "}
            {total > 0 ? `${total} cohort${total === 1 ? "" : "s"}.` : null}
          </p>
        </div>
        <div className="overview-hero-actions">
          <button type="button" className="btn primary" disabled={creating} onClick={openCreateModal}>
            <FontAwesomeIcon icon={faPlus} /> New cohort
          </button>
        </div>
      </header>

      {error ? (
        <div className="warn-banner" role="alert">
          {error}
          {upgradeCta ? (
            <>
              {" "}
              <Link to="/teacher/billing">Manage billing</Link>
            </>
          ) : null}
        </div>
      ) : null}

      {createOpen ? (
        <dialog className="teacher-modal" open aria-labelledby="create-cohort-title">
          <div className="teacher-modal-card">
            <h2 id="create-cohort-title" className="teacher-modal-title">
              Start cohort
            </h2>
            {courses.length === 0 ? (
              <>
                <p className="muted">
                  Create an active <Link to="/teacher/courses">course</Link> first.
                </p>
                <div className="teacher-modal-actions">
                  <button type="button" className="btn secondary" onClick={closeCreateModal}>
                    Close
                  </button>
                </div>
              </>
            ) : (
              <form className="stack-form tight" onSubmit={handleCreate}>
                <label className="field">
                  <span className="field-label">Course</span>
                  <select
                    className="input"
                    value={pickTemplateId}
                    onChange={(ev) => setPickTemplateId(ev.target.value)}
                    disabled={creating}
                    required
                  >
                    {courses.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.title}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span className="field-label">Cohort label</span>
                  <input
                    className="input"
                    value={newLabel}
                    onChange={(ev) => setNewLabel(ev.target.value)}
                    placeholder="e.g. Period 3, Spring 2026"
                    required
                    disabled={creating}
                    autoFocus
                  />
                </label>
                <label className="muted small" style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                  <input
                    type="checkbox"
                    checked={seedLessonPlans}
                    onChange={(ev) => setSeedLessonPlans(ev.target.checked)}
                    disabled={creating}
                  />
                  Seed lesson plan from all template lessons (unreleased)
                </label>
                <div className="teacher-modal-actions">
                  <button type="button" className="btn secondary" disabled={creating} onClick={closeCreateModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn primary" disabled={!bearer || creating || !newLabel.trim()}>
                    {creating ? "Creating…" : "Create cohort"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </dialog>
      ) : null}

      <section className="teacher-panel teacher-panel--flush">
        <h2 className="teacher-panel-title">Your current cohorts</h2>
        {loading ? (
          <p className="muted teacher-pad">Loading…</p>
        ) : activeCohorts.length === 0 ? (
          <p className="muted teacher-pad">
            No active cohorts. Click <strong>New cohort</strong> above to start one from a course.
          </p>
        ) : (
          <div className="teacher-table-wrap cohort-table-wrap">
            <table className="teacher-table cohort-table">
              {tableHead}
              <CohortRows
                cohorts={activeCohorts}
                coursesById={coursesById}
                concludingId={concludingId}
                deletingId={null}
                onConclude={(c) => void handleConclude(c)}
                showProgress
              />
            </table>
          </div>
        )}
      </section>

      {!loading && previousCohorts.length > 0 ? (
        <section className="teacher-panel teacher-panel--flush">
          <h2 className="teacher-panel-title">Previous cohorts</h2>
          <p className="muted small teacher-pad" style={{ paddingTop: 0, marginTop: "-0.5rem" }}>
            Concluded cohorts kept for your records. Delete permanently when you no longer need the data.
          </p>
          <div className="teacher-table-wrap cohort-table-wrap">
            <table className="teacher-table cohort-table">
              {tableHead}
              <CohortRows
                cohorts={previousCohorts}
                coursesById={coursesById}
                concludingId={null}
                deletingId={deletingId}
                onDelete={(c) => void handleDelete(c)}
                showProgress={false}
              />
            </table>
          </div>
        </section>
      ) : null}
    </div>
  );
}
